import { createContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export type PlatformRole = "owner" | "cs" | "support" | "finance";

export interface PlatformAuthState {
  loading: boolean;
  session: Session | null;
  role: PlatformRole | null;
  isPlatformAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

export const PlatformAuthContext = createContext<PlatformAuthState | undefined>(undefined);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<PlatformRole | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRoleFor(userId: string) {
      const { data } = await supabase
        .from("klubora_platform_users")
        .select("platform_role, active")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (data && data.active) {
        setRole(data.platform_role as PlatformRole);
      } else {
        setRole(null);
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      if (data.session?.user) {
        loadRoleFor(data.session.user.id).finally(() => {
          if (!cancelled) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        // Block AuthGate while we re-resolve the platform role for the new session.
        // Without this, navigate("/") after signIn would race the role lookup and
        // briefly show isPlatformAdmin=false, triggering an /access-denied redirect.
        setLoading(true);
        loadRoleFor(newSession.user.id).finally(() => {
          if (!cancelled) setLoading(false);
        });
      } else {
        setRole(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value: PlatformAuthState = {
    loading,
    session,
    role,
    isPlatformAdmin: role !== null,
    signIn,
    signOut,
  };

  return (
    <PlatformAuthContext.Provider value={value}>
      {children}
    </PlatformAuthContext.Provider>
  );
}
